import { ActionIcon, Anchor, Divider, Group, Popover, Stack, Text } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import { useState } from 'react';
import { IconCloudUpload, IconDotsVertical, IconDownload, IconTrash } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatDate } from 'src/meetings/utils/date-utils';
import { UploadedFile } from '../../hooks/file-uploads/types';

const DATE_FORMAT = 'YYYY-MM-DD';

type DocumentActionsPopoverProps = {
  file: UploadedFile;
  onDownload: (file: UploadedFile) => void;
  onDelete: (file: UploadedFile) => void;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
  onSetResolved: (file: UploadedFile, isResolved: boolean) => void;
  onUploadToSharepoint?: (file: UploadedFile) => void;
  readOnly?: boolean;
};

export default function DocumentActionsPopover({
  file,
  onDownload,
  onDelete,
  onUpdateDate,
  onSetResolved,
  onUploadToSharepoint,
  readOnly = false,
}: DocumentActionsPopoverProps) {
  if (file.id.startsWith('pending-')) return null;

  const reviewedLabel = file.isResolved ? 'Set as not reviewed' : 'Set as reviewed';

  return (
    <Popover position="bottom-end" radius={16} shadow="sm" withinPortal>
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          color="gray.10"
          aria-label="Document actions"
          data-testid="document-card-menu"
        >
          <IconDotsVertical size={24} color="var(--mantine-color-gray-10)" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown
        p="md"
        bg="white"
        style={{ border: '1px solid var(--mantine-color-gray-1)', backdropFilter: 'blur(12px)' }}
      >
        <Stack gap="sm" w={240}>
          <Stack gap="xxxs">
            <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
              Creation date
            </Text>
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                {formatDate(file.documentDate)}
              </Text>
              {!readOnly && <EditCreationDate file={file} onUpdateDate={onUpdateDate} />}
            </Group>
          </Stack>
          <Divider color="gray.1" />
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Group gap="xxs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="gray.10"
                aria-label="Download document"
                data-testid="doc-actions-download"
                onClick={() => onDownload(file)}
              >
                <IconDownload size={20} />
              </ActionIcon>
              {!readOnly && (
                <ActionIcon
                  variant="subtle"
                  color="gray.10"
                  aria-label="Delete document"
                  data-testid="doc-actions-delete"
                  onClick={() => onDelete(file)}
                >
                  <IconTrash size={20} />
                </ActionIcon>
              )}
              {!readOnly && onUploadToSharepoint && !file.file.isFolder && (
                <ActionIcon
                  variant="subtle"
                  color="gray.10"
                  aria-label="Upload to SharePoint"
                  data-testid="doc-actions-upload-sharepoint"
                  onClick={() => onUploadToSharepoint(file)}
                >
                  <IconCloudUpload size={20} />
                </ActionIcon>
              )}
            </Group>
            {!readOnly && (
              <Anchor
                component="button"
                type="button"
                underline="never"
                size="xs"
                fw={FONT_WEIGHTS.medium}
                c="brand.6"
                data-testid="doc-actions-set-reviewed"
                onClick={() => onSetResolved(file, !file.isResolved)}
              >
                {reviewedLabel}
              </Anchor>
            )}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function EditCreationDate({
  file,
  onUpdateDate,
}: {
  file: UploadedFile;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
}) {
  const [opened, setOpened] = useState(false);

  const selectedDate = file.documentDate ? dayjs(file.documentDate).format(DATE_FORMAT) : null;

  const handleChange = (next: string | null) => {
    if (!next) return;
    onUpdateDate(file, new Date(`${dayjs(next).format(DATE_FORMAT)}T00:00:00Z`));
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      radius={12}
      shadow="xl"
      withinPortal
    >
      <Popover.Target>
        <Anchor
          component="button"
          type="button"
          underline="never"
          size="xs"
          fw={FONT_WEIGHTS.medium}
          c="brand.6"
          data-testid="doc-actions-edit-date"
          onClick={() => setOpened(prev => !prev)}
        >
          Edit
        </Anchor>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <DatePicker
          value={selectedDate}
          defaultDate={selectedDate ?? undefined}
          onChange={handleChange}
          maxDate={dayjs(file.createdAt).format(DATE_FORMAT)}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
